/**
 * Por que um número saiu como N/D — a regra, sem React.
 *
 * As rotas de métrica não dizem por que um número faltou: uma loja sem propriedade GA4
 * escolhida recebe o card **zerado**, e não um erro. Quem sabe a diferença entre "falta
 * integrar" e "não houve movimento no período" é `GET /external-accounts/selected`, que lista
 * as contas escolhidas da loja por produto. Esta função cruza as duas coisas: o que o número
 * precisa e o que a loja tem.
 */

/** Os cinco produtos que alimentam as telas, na grafia da tela. */
export type FonteDeDados =
  | "ga4"
  | "google_ads"
  | "search_console"
  | "meta_ads"
  | "instagram";

/**
 * Um requisito do número: uma fonte, ou uma lista em que **qualquer uma** basta.
 *
 * `["ga4", MIDIA_PAGA]` lê-se "precisa do GA4 **e** de Google Ads ou Meta Ads" — é o ROAS do
 * funil, que divide a receita do GA4 pelo investimento somado das duas mídias.
 */
export type RequisitoDeFonte = FonteDeDados | readonly FonteDeDados[];

/** Investimento: qualquer uma das duas mídias pagas já produz o número. */
export const MIDIA_PAGA: readonly FonteDeDados[] = ["google_ads", "meta_ads"];

export const NOME_DA_FONTE: Readonly<Record<FonteDeDados, string>> = {
  ga4: "Google Analytics",
  google_ads: "Google Ads",
  search_console: "Search Console",
  meta_ads: "Meta Ads",
  instagram: "Instagram",
};

export type FamiliaDeConexao = "google" | "meta";

const FAMILIA_DA_FONTE: Readonly<Record<FonteDeDados, FamiliaDeConexao>> = {
  ga4: "google",
  google_ads: "google",
  search_console: "google",
  meta_ads: "meta",
  instagram: "meta",
};

/**
 * `SelectedExternalAccount.type` → fonte. São os valores de `external_accounts.source`
 * (`ga4`, `instagram_insights`), mais as grafias antigas que a seleção ainda aceita.
 */
const FONTE_DO_TIPO_DE_CONTA: Readonly<Record<string, FonteDeDados>> = {
  ga4: "ga4",
  google_analytics: "ga4",
  google_ads: "google_ads",
  search_console: "search_console",
  meta_ads: "meta_ads",
  instagram_insights: "instagram",
  instagram: "instagram",
};

/** O que a loja tem: fontes com conta escolhida, e famílias com o consentimento vencido. */
export interface EstadoDasFontes {
  readonly configuradas: ReadonlySet<FonteDeDados>;
  readonly familiasExpiradas: ReadonlySet<FamiliaDeConexao>;
}

export const estadoDasFontes = (
  tiposDeConta: readonly string[],
  familiasExpiradas: readonly FamiliaDeConexao[],
): EstadoDasFontes => {
  const configuradas = new Set<FonteDeDados>();
  for (const tipo of tiposDeConta) {
    const fonte = FONTE_DO_TIPO_DE_CONTA[tipo.trim().toLowerCase()];
    if (fonte) configuradas.add(fonte);
  }
  return { configuradas, familiasExpiradas: new Set(familiasExpiradas) };
};

export type MotivoDoIndisponivel =
  | {
      readonly tipo: "sem-integracao";
      readonly faltam: readonly RequisitoDeFonte[];
    }
  | {
      readonly tipo: "conexao-expirada";
      readonly fontes: readonly FonteDeDados[];
    }
  | { readonly tipo: "erro" }
  | { readonly tipo: "sem-dados"; readonly fontes: readonly FonteDeDados[] }
  | { readonly tipo: "indeterminado" };

const alternativas = (requisito: RequisitoDeFonte): readonly FonteDeDados[] =>
  typeof requisito === "string" ? [requisito] : requisito;

/**
 * Os requisitos que a loja não cumpre.
 *
 * É o que transforma um zero em N/D: a rota de métrica devolve o card zerado para quem não
 * integrou o produto, e "R$ 0,00" ali afirma um movimento que ninguém mediu.
 */
export const requisitosFaltando = (
  requisitos: readonly RequisitoDeFonte[],
  estado: EstadoDasFontes,
): readonly RequisitoDeFonte[] =>
  requisitos.filter(
    (requisito) =>
      !alternativas(requisito).some((fonte) => estado.configuradas.has(fonte)),
  );

/**
 * A ordem das perguntas é a ordem do que o usuário consegue resolver:
 *
 * 1. **Falta integrar** vem antes do erro, porque é a explicação acionável — e porque, sem a
 *    integração, a rota nem erra: devolve zero.
 * 2. **Conexão vencida** — a conta está escolhida, mas o token da família foi invalidado.
 * 3. **Erro** da requisição do número.
 * 4. **Sem dados** — tudo configurado e a busca respondeu; o período simplesmente não teve.
 *
 * `estado` nulo quer dizer que ainda não se sabe o que a loja tem (a consulta não voltou, ou
 * falhou). Aí não se afirma nada sobre integração: sai o erro, se houve, ou o texto genérico.
 */
export const motivoDoIndisponivel = (entrada: {
  readonly requisitos: readonly RequisitoDeFonte[];
  readonly estado: EstadoDasFontes | null;
  readonly erro?: boolean;
}): MotivoDoIndisponivel => {
  const { requisitos, estado, erro = false } = entrada;

  if (estado === null) {
    return erro ? { tipo: "erro" } : { tipo: "indeterminado" };
  }

  const faltam = requisitosFaltando(requisitos, estado);
  if (faltam.length > 0) return { tipo: "sem-integracao", faltam };

  const usadas = requisitos.flatMap((requisito) =>
    alternativas(requisito).filter((fonte) => estado.configuradas.has(fonte)),
  );
  const fontes = [...new Set(usadas)];

  const expiradas = fontes.filter((fonte) =>
    estado.familiasExpiradas.has(FAMILIA_DA_FONTE[fonte]),
  );
  if (expiradas.length > 0) {
    return { tipo: "conexao-expirada", fontes: expiradas };
  }

  if (erro) return { tipo: "erro" };
  return { tipo: "sem-dados", fontes };
};

/** "A", "A e B", "A, B e C". */
const listar = (itens: readonly string[], conector: "e" | "ou"): string => {
  if (itens.length <= 1) return itens[0] ?? "";
  return `${itens.slice(0, -1).join(", ")} ${conector} ${itens[itens.length - 1]}`;
};

const nomeDoRequisito = (requisito: RequisitoDeFonte): string =>
  listar(
    alternativas(requisito).map((fonte) => NOME_DA_FONTE[fonte]),
    "ou",
  );

export const textoDoMotivo = (motivo: MotivoDoIndisponivel): string => {
  switch (motivo.tipo) {
    case "sem-integracao":
      return `Falta integrar ${listar(motivo.faltam.map(nomeDoRequisito), "e")} nesta loja. Conecte na tela Integrações para ver este número.`;
    case "conexao-expirada":
      return `A conexão com ${listar(
        motivo.fontes.map((fonte) => NOME_DA_FONTE[fonte]),
        "e",
      )} expirou. Reconecte na tela Integrações para voltar a ver este número.`;
    case "erro":
      return "Não conseguimos carregar este número agora. Tente atualizar a página em alguns instantes.";
    case "sem-dados":
      return motivo.fontes.length > 0
        ? `${listar(
            motivo.fontes.map((fonte) => NOME_DA_FONTE[fonte]),
            "e",
          )} não registrou dados para o período selecionado.`
        : "Não há dados para o período selecionado.";
    case "indeterminado":
      return "Não conseguimos processar esta informação. Verifique a conexão na tela Integrações e a configuração das suas fontes nos portais (Google, Meta).";
  }
};

/** O motivo pede ação do usuário (integrar, reconectar, tentar de novo)? */
export const motivoPedeAtencao = (motivo: MotivoDoIndisponivel): boolean =>
  motivo.tipo === "sem-integracao" ||
  motivo.tipo === "conexao-expirada" ||
  motivo.tipo === "erro";
